import type { RestaurantCategoryName } from '../model/schema';

/**
 * Branch rubric ids behind an entity/payload bag (`restaurant_category_ids`).
 * Reads both wire shapes the merchant RPCs send:
 * - `POST /api/v1/restaurants` — a plain array (`["uuid", …]`);
 * - `PATCH /api/v1/restaurants/{id}` — the `{ids: [...]}` wrapper (absent =
 *   the rubrics stay untouched, `{ids: []}` = every rubric removed).
 *
 * Blank entries are dropped, duplicates collapsed, order kept.
 */
export function restaurantCategoryIdsOf(
  bag: Record<string, unknown> | null | undefined,
): string[] {
  const value: unknown = bag?.restaurant_category_ids;
  const raw: unknown = Array.isArray(value)
    ? value
    : (value as Record<string, unknown> | null)?.ids;
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const id = item.trim();
    if (id === '' || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * Rubric display name — uz first (the admin-list canonical), then ru, en.
 * Unresolved rubrics fall back to the short id (`93c4dd52…`).
 */
export function categoryDisplayName(
  name: RestaurantCategoryName | null | undefined,
  fallbackId: string,
): string {
  const uz = name?.uz?.trim();
  if (uz !== undefined && uz !== '') return uz;
  const ru = name?.ru?.trim();
  if (ru !== undefined && ru !== '') return ru;
  const en = name?.en?.trim();
  if (en !== undefined && en !== '') return en;
  const trimmed = fallbackId.trim();
  if (trimmed === '') return '—';
  return trimmed.length > 8 ? `${trimmed.slice(0, 8)}…` : trimmed;
}
